import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { getActivePartnerships } from "../../services/partnershipsApi";
import { getPhoto } from "../../storageService";
import "../Providers/PartnerCard.css";
import "./PartnerBenefitsBox.css";

/**
 * Box "Beneficios Partners" visible para cualquier User.isProvider == true.
 * Lista los partnerships activos con su descripción y link al perfil del partner.
 * No requiere que el provider sea partner — un pintor ve "Miksa: 10% off en pinturas".
 */
const PartnerBenefitsBox = () => {
    const [partnerships, setPartnerships] = useState([]);
    const [photoUrls, setPhotoUrls] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await getActivePartnerships();
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                setPartnerships(list);

                // Resolver fotos del partner (avatar) en paralelo
                const entries = await Promise.all(
                    list.map(async (p) => {
                        const path = p.partnerPhoto || p.bannerPhoto;
                        if (!path) return [p.id, null];
                        try { return [p.id, await getPhoto(path)]; }
                        catch { return [p.id, null]; }
                    })
                );
                if (cancelled) return;
                setPhotoUrls(Object.fromEntries(entries));
            } catch (err) {
                console.warn("No se pudieron cargar partnerships activos:", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // No mostrar nada si no hay partnerships activos (evitamos un card vacío y triste)
    if (!loading && partnerships.length === 0) return null;

    return (
        <div className="profile-card partner-benefits-card">
            <div className="profile-card-header">
                <h3 className="profile-subheading partner-benefits-card__title">
                    <Sparkles size={16} />
                    Beneficios para Proveedores
                </h3>
                <Link to="/partners" className="partner-benefits-card__see-all">
                    Ver todos
                    <ArrowRight size={14} />
                </Link>
            </div>
            <div className="profile-card-body">
                <p className="partner-benefits-card__intro">
                    Como proveedor de <span className="brand">CONTACTAME</span>, accedés a beneficios y
                    descuentos exclusivos ofrecidos por nuestros Partners.
                </p>

                {loading ? (
                    <p className="partner-benefits-card__loading">Cargando beneficios...</p>
                ) : (
                    <ul className="partner-benefits-list">
                        {partnerships.map((p) => {
                            const photo = photoUrls[p.id];
                            const name = p.partnerName || p.productProviderId;
                            return (
                                <li key={p.id} className="partner-benefit-item">
                                    <Link
                                        to={`/${p.productProviderId}`}
                                        state={{ fromBenefits: true }}
                                        className="partner-benefit-item__link"
                                    >
                                        {photo ? (
                                            <img
                                                src={photo}
                                                alt={name}
                                                className="partner-benefit-item__avatar"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="partner-benefit-item__avatar partner-benefit-item__avatar--plain" aria-hidden="true">
                                                {name?.[0]?.toUpperCase() || "?"}
                                            </div>
                                        )}
                                        <div className="partner-benefit-item__body">
                                            <span className="partner-benefit-item__name">{name}</span>
                                            {p.description && (
                                                <span className="partner-benefit-item__description">
                                                    {p.description}
                                                </span>
                                            )}
                                        </div>
                                        <ArrowRight size={16} className="partner-benefit-item__arrow" />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default PartnerBenefitsBox;
